import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    supplierContactCreateUrl,
    supplierContactDetailUrl,
    supplierContactListUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierContact, SupplierContactType} from "~/pages/supplier-360/types/Supplier360Types";

export type AddSupplierContactVariables = {
    entityId: number;
    contact_type: SupplierContactType;
    /** Per-assignment role. Blank for most `relation_manager` rows — there the type is the role. */
    role?: string;
    /** Must name an EXISTING account (§2.23, re-modeled 2026-10-23 per the user) — no other path. */
    user_id: number;
    /** Notification copy only — never sent. */
    displayName: string;
};

export type RemoveSupplierContactVariables = {
    entityId: number;
    contactId: number;
    /** Notification copy only — never sent. */
    displayName: string;
};

/**
 * `POST supplier_360/contact/` — checks one person against one multi-select (docs §2.23). The row
 * is the checkmark, so this is what "select" means in these fields; the person (a real `User`)
 * always already exists — `user_id` is the only identity path (re-modeled 2026-10-23 per the user).
 *
 * `(entity, contact_type, user)` is unique, so re-checking someone already in the same field
 * `400`s — the caller never offers an already-checked option as an add.
 */
export function useAddSupplierContactMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierContact, DefaultError, AddSupplierContactVariables>({
        mutationFn: async ({entityId, contact_type, role, user_id}) => {
            const {data} = await queryHttp.post<SupplierContact>(supplierContactCreateUrl(), {
                entity: entityId,
                contact_type,
                role: role ?? '',
                user_id,
            });
            return data;
        },
        onSuccess: (_created, {entityId, displayName}) => {
            void queryClient.invalidateQueries({queryKey: [supplierContactListUrl(entityId)]});
            notificationStore.show(`${displayName} selected.`, {
                severity: 'success',
                id: `supplier-contact-add-success-${entityId}`,
                autoHideDuration: 3000,
            });
        },
    });
}

/**
 * `DELETE supplier_360/contact/{id}/` — unchecks one person in one multi-select (docs §2.23).
 * Only the assignment goes: the `User` itself, and every other entity that checked them,
 * survive.
 */
export function useRemoveSupplierContactMutation() {
    const queryClient = useQueryClient();

    return useMutation<void, DefaultError, RemoveSupplierContactVariables>({
        mutationFn: async ({contactId}) => {
            await queryHttp.delete(supplierContactDetailUrl(contactId));
        },
        onSuccess: (_void, {entityId, displayName}) => {
            void queryClient.invalidateQueries({queryKey: [supplierContactListUrl(entityId)]});
            // Never "removed" — the person stays, only their checkmark on this supplier goes.
            notificationStore.show(`${displayName} unselected.`, {
                severity: 'success',
                id: `supplier-contact-remove-success-${entityId}`,
                autoHideDuration: 3000,
            });
        },
    });
}
